/**
 * rp-quiz-funnel engine.
 * Reads a config object emitted by the section's Liquid (window.RP_QUIZ_CONFIGS[id])
 * and drives a client-side, multi-step quiz: capture -> branch -> score -> redirect.
 * No framework, no build step — vanilla JS so it drops straight into a Shopify theme.
 */
(function () {
  'use strict';

  function initQuiz(root) {
    var id = root.getAttribute('data-quiz-id');
    var cfg = (window.RP_QUIZ_CONFIGS || {})[id];
    if (!cfg) return;

    var state = {
      stepIndex: 0,
      answers: {},
      order: [] // ids of steps actually shown, for back/progress
    };

    // ---- response tracking (Google Sheet via Apps Script webhook) ----
    var sessionStorageKey = 'rp_quiz_session_' + id;
    var sessionId;
    try {
      sessionId = window.sessionStorage.getItem(sessionStorageKey);
      if (!sessionId) {
        sessionId = 'rq_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
        window.sessionStorage.setItem(sessionStorageKey, sessionId);
      }
    } catch (err) {
      sessionId = 'rq_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
    }

    function logEvent(eventType, extra) {
      var url = cfg.analytics_webhook_url;
      if (!url) return;
      try {
        var payload = {
          event: eventType,
          session_id: sessionId,
          quiz_id: id,
          ts: new Date().toISOString(),
          page_url: window.location.href
        };
        for (var k in (extra || {})) { payload[k] = extra[k]; }
        fetch(url, {
          method: 'POST',
          mode: 'no-cors',
          keepalive: true,
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify(payload)
        });
      } catch (err) {
        // analytics must never break the quiz
      }
    }

    var viewport = root.querySelector('.rp-quiz__viewport');
    var progressFill = root.querySelector('.rp-quiz__progress-fill');
    var stepEls = {};
    root.querySelectorAll('.rp-quiz__step').forEach(function (el) {
      stepEls[el.getAttribute('data-step-id')] = el;
    });

    var questionSteps = cfg.steps.filter(function (s) {
      return s.type === 'open_text' || s.type === 'choice' || s.type === 'compound_age_breed';
    });
    var totalQuestions = questionSteps.length;

    // ---- progress bar: weighted across every non-landing step, not just questions ----
    // The bar advances on every screen (questions + loading/score/objection/etc.), so it
    // never sits frozen through the back half of the quiz. The first 4 questions count
    // double weight so the bar jumps further per tap early on (biggest drop-off point),
    // while still landing on exactly 100% at the final step.
    var progressWeightedSteps = cfg.steps.filter(function (s) { return s.type !== 'landing'; });
    var qSeen = 0;
    var progressWeights = progressWeightedSteps.map(function (s) {
      var isQuestion = (s.type === 'open_text' || s.type === 'choice' || s.type === 'compound_age_breed');
      if (isQuestion) {
        qSeen += 1;
        return qSeen <= 4 ? 2 : 1;
      }
      return 1;
    });
    var progressTotalWeight = progressWeights.reduce(function (a, b) { return a + b; }, 0);
    var progressPctById = {};
    (function () {
      var running = 0;
      progressWeightedSteps.forEach(function (s, i) {
        running += progressWeights[i];
        progressPctById[s.id] = (running / progressTotalWeight) * 100;
      });
    })();

    function interpolate(str) {
      if (!str) return str;
      return str.replace(/\[\[\s*([a-zA-Z0-9_]+)\s*\]\]/g, function (m, key) {
        var v = state.answers[key];
        if (v === undefined || v === null || v === '') return cfg.fallbacks && cfg.fallbacks[key] ? cfg.fallbacks[key] : m;
        return Array.isArray(v) ? v.join(', ') : v;
      });
    }

    function applyMergeTags(container) {
      container.querySelectorAll('[data-merge]').forEach(function (el) {
        el.textContent = interpolate(el.getAttribute('data-merge-src'));
      });
    }

    function showStep(stepId) {
      Object.keys(stepEls).forEach(function (k) {
        stepEls[k].classList.toggle('is-active', k === stepId);
      });
      var step = cfg.steps.find(function (s) { return s.id === stepId; });
      if (step) {
        // renderDynamicBits runs first: it can write NEW state.answers.* values
        // (e.g. weight-tier fields on the offer screen) that this screen's own
        // merge tags need to read a moment later.
        renderDynamicBits(step, stepEls[stepId]);
        applyMergeTags(stepEls[stepId]);
      }
      var qIdx = questionSteps.findIndex(function (s) { return s.id === stepId; });
      if (progressFill && progressPctById[stepId] !== undefined) {
        progressFill.style.width = progressPctById[stepId] + '%';
      }
      viewport.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.scrollTo(0, 0);

      logEvent('step_view', {
        step_id: stepId,
        step_type: step ? step.type : '',
        step_number: qIdx > -1 ? qIdx + 1 : null,
        total_questions: totalQuestions
      });
    }

    function goTo(stepId) {
      state.order.push(stepId);
      showStep(stepId);
    }

    function nextFrom(currentId) {
      var leavingStep = cfg.steps.find(function (s) { return s.id === currentId; });
      if (leavingStep && (leavingStep.type === 'open_text' || leavingStep.type === 'choice' || leavingStep.type === 'compound_age_breed')) {
        logEvent('answer', {
          step_id: currentId,
          step_type: leavingStep.type,
          answers_so_far: state.answers
        });
      }
      var idx = cfg.steps.findIndex(function (s) { return s.id === currentId; });
      var next = cfg.steps[idx + 1];
      if (next) goTo(next.id);
    }

    function back() {
      if (state.order.length < 2) return;
      state.order.pop();
      var prev = state.order[state.order.length - 1];
      showStep(prev);
    }

    // ---- dynamic per-step rendering (insight branch text, score, tables) ----
    function renderDynamicBits(step, el) {
      if (step.insight_map) {
        var insightEl = el.querySelector('.rp-quiz__insight');
        if (insightEl) {
          var branchVar = step.insight_var;
          var val = state.answers[branchVar];
          var picked = null;
          if (Array.isArray(val)) {
            for (var i = 0; i < val.length; i++) {
              if (step.insight_map[val[i]]) { picked = step.insight_map[val[i]]; break; }
            }
          } else if (val) {
            picked = step.insight_map[val];
          }
          if (!picked) picked = step.insight_default;
          if (picked) {
            var t = insightEl.querySelector('.rp-quiz__insight-title');
            var b = insightEl.querySelector('.rp-quiz__insight-body');
            if (t) t.textContent = interpolate(picked.title);
            if (b) b.textContent = interpolate(picked.body);
          }
        }
      }
      if (step.type === 'score') renderScore(step, el);
      if (step.type === 'offer') renderOffer(step, el);
      if (step.type === 'loading') renderLoadingRing(step, el);
      if (step.type === 'plan_loading') {
        renderLoadingRing(step, el);
        renderBuildChecklist(step, el);
      }
    }

    function renderBuildChecklist(step, el) {
      var items = el.querySelectorAll('.rp-quiz__build-item');
      if (!items.length) return;
      var total = step.duration_ms || 4400;
      var perItem = Math.max(400, Math.floor(total / items.length));
      items.forEach(function (item, idx) {
        item.classList.remove('is-loading', 'is-done');
        var startAt = idx * perItem;
        var doneAt = startAt + Math.floor(perItem * 0.8);
        setTimeout(function () {
          if (el.classList.contains('is-active')) item.classList.add('is-loading');
        }, startAt);
        setTimeout(function () {
          if (el.classList.contains('is-active')) {
            item.classList.remove('is-loading');
            item.classList.add('is-done');
          }
        }, doneAt);
      });
    }

    function renderLoadingRing(step, el) {
      var pctEl = el.querySelector('.rp-quiz__ring-pct');
      var fillEl = el.querySelector('.rp-quiz__ring-fill');
      if (!pctEl || !fillEl) return;
      var duration = step.duration_ms || 5000;
      var circumference = 2 * Math.PI * 60;
      fillEl.style.strokeDasharray = circumference;
      fillEl.style.strokeDashoffset = circumference;
      pctEl.textContent = '0%';
      var start = null;
      function tick(ts) {
        if (!el.classList.contains('is-active')) return;
        if (!start) start = ts;
        var elapsed = ts - start;
        var pct = Math.min(100, Math.round((elapsed / duration) * 100));
        pctEl.textContent = pct + '%';
        fillEl.style.strokeDashoffset = circumference * (1 - pct / 100);
        if (elapsed < duration) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    }

    function computeScore() {
      var rules = cfg.score_rules || {};
      var score = rules.base || 20;
      var symptoms = state.answers.symptoms || [];
      score += Math.min(symptoms.length, rules.symptom_cap || 4) * (rules.per_symptom || 8);
      var duration = state.answers.duration;
      if (duration && rules.duration_points && rules.duration_points[duration]) {
        score += rules.duration_points[duration];
      }
      if (state.answers.diet === rules.dry_kibble_value && rules.dry_kibble_points) {
        score += rules.dry_kibble_points;
      }
      if (state.answers.trigger === rules.trigger_value && rules.trigger_points) {
        score += rules.trigger_points;
      }
      return Math.max(0, Math.min(100, score));
    }

    function renderScore(step, el) {
      var score = computeScore();
      state.answers.itch_score = score;
      var band = score >= 70 ? 'severe' : (score >= 40 ? 'moderate' : 'mild');
      state.answers.itch_band = band.toUpperCase();
      var numberEl = el.querySelector('.rp-quiz__score-number');
      if (numberEl) numberEl.textContent = score;
      var badgeEl = el.querySelector('.rp-quiz__score-badge');
      if (badgeEl) badgeEl.textContent = band.toUpperCase();
      var markerEl = el.querySelector('.rp-quiz__score-marker');
      if (markerEl) markerEl.style.left = score + '%';

      var driverWrap = el.querySelector('.rp-quiz__drivers');
      if (driverWrap) {
        driverWrap.innerHTML = '';
        var drivers = [];

        // 1. Breed — always fires
        if (step.breed_driver) {
          var bd = step.breed_driver;
          var breed = state.answers.breed;
          var mixedMatches = String(bd.mixed_match || '').split('|').map(function (m) { return m.trim(); });
          if (breed && mixedMatches.indexOf(breed) > -1) {
            drivers.push({ title: bd.mixed_title, body: bd.mixed_body });
          } else if (breed) {
            drivers.push({ title: interpolate(bd.title), body: bd.body });
          }
        }

        // 2. Duration — always fires (matches Q5 answer)
        if (step.duration_drivers && state.answers.duration && step.duration_drivers[state.answers.duration]) {
          drivers.push(step.duration_drivers[state.answers.duration]);
        }

        // 3. Trigger — always fires (matches Q8 answer)
        if (step.trigger_drivers && state.answers.trigger && step.trigger_drivers[state.answers.trigger]) {
          drivers.push(step.trigger_drivers[state.answers.trigger]);
        }

        // 4. What they've tried — always fires, matched to the specific thing(s) they picked
        if (step.tried_drivers || step.tried_driver_none) {
          var tried = state.answers.tried || [];
          var triedSomething = tried.length > 0 && !(tried.length === 1 && tried[0] === 'Nothing yet');
          var picked = null;
          if (triedSomething && step.tried_drivers) {
            for (var ti = 0; ti < tried.length; ti++) {
              if (step.tried_drivers[tried[ti]]) { picked = step.tried_drivers[tried[ti]]; break; }
            }
          }
          if (!picked) picked = step.tried_driver_none;
          if (picked) drivers.push(picked);
        }

        drivers.forEach(function (d) {
          if (!d || !d.title) return;
          var row = document.createElement('div');
          row.className = 'rp-quiz__driver-row';
          row.innerHTML = '<strong></strong><span></span>';
          row.querySelector('strong').textContent = d.title;
          row.querySelector('span').textContent = d.body;
          driverWrap.appendChild(row);
        });
      }
    }

    function renderOffer(step, el) {
      var weight = state.answers.dog_weight;
      var tier = (cfg.weight_tiers || {})[weight] || (cfg.weight_tiers ? cfg.weight_tiers[Object.keys(cfg.weight_tiers)[0]] : null);
      if (!tier) return;

      // Populate state.answers BEFORE merge tags run on this screen (see showStep ordering)
      state.answers.tier_days_label = tier.days_label;
      state.answers.tier_plan_bags = tier.plan_bags;
      state.answers.tier_pct = tier.pct;
      state.answers.tier_alt_days_label = tier.alt_days_label;
      state.answers.tier_alt_plan_bags = tier.alt_plan_bags;
      state.answers.tier_alt_pct = tier.alt_pct;
      state.answers.tier_daily_pct = tier.daily_pct;

      var badgeEl = el.querySelector('[data-role="plan-badge"]');
      if (badgeEl && step.plan_badge_template) badgeEl.textContent = interpolate(step.plan_badge_template);

      var planNameEl = el.querySelector('[data-role="plan-name"]');
      if (planNameEl) planNameEl.textContent = tier.days_label + ' Plan';
      var planSubEl = el.querySelector('[data-role="plan-sub"]');
      if (planSubEl) planSubEl.textContent = tier.plan_bags + ' · ' + (step.plan_recommended_suffix || 'the full transformation');
      var planPctEl = el.querySelector('[data-role="plan-pct"]');
      if (planPctEl) planPctEl.textContent = tier.pct + '% OFF';

      var altNameEl = el.querySelector('[data-role="alt-name"]');
      if (altNameEl) altNameEl.textContent = tier.alt_days_label + ' Plan';
      var altSubEl = el.querySelector('[data-role="alt-sub"]');
      if (altSubEl) altSubEl.textContent = tier.alt_plan_bags + ' · ' + (step.plan_alt_suffix || 'a first look');
      var altPctEl = el.querySelector('[data-role="alt-pct"]');
      if (altPctEl) altPctEl.textContent = tier.alt_pct + '% OFF';

      var savingsEl = el.querySelector('[data-role="savings-copy"]');
      if (savingsEl && step.savings_template) savingsEl.textContent = interpolate(step.savings_template);

      var selected = state.answers.selected_plan || 'default';
      var defaultCard = el.querySelector('[data-role="plan-card-default"]');
      var altCard = el.querySelector('[data-role="plan-card-alt"]');
      if (defaultCard) defaultCard.classList.toggle('is-selected', selected === 'default');
      if (altCard) altCard.classList.toggle('is-selected', selected === 'alt');

      var ctaEl = el.querySelector('[data-role="offer-cta"]');
      if (ctaEl) {
        ctaEl.onclick = function () {
          var url = cfg.redirect_base_url || '#';
          var params = new URLSearchParams();
          if (state.answers.dog_name) params.set('dog_name', state.answers.dog_name);
          if (state.answers.breed) params.set('breed', state.answers.breed);
          // PDP weight buttons match handle-style values ("95+ lbs" -> "95-lbs", "1–25 lbs" -> "1-25-lbs").
          // A quiz can show other units and map each answer to the product's weight (weight_pdp_values).
          if (state.answers.dog_weight) {
            var pdpWeight = (cfg.weight_pdp_values || {})[state.answers.dog_weight] || state.answers.dog_weight;
            params.set('weight', String(pdpWeight).toLowerCase()
              .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''));
          }
          params.set('plan', selected === 'alt' ? (tier.alt_days_label + ' Plan') : (tier.days_label + ' Plan'));
          params.set('utm_source', 'quiz');
          logEvent('conversion', {
            step_id: step.id,
            answers_so_far: state.answers,
            destination_url: url
          });
          window.location.href = url + (url.indexOf('?') > -1 ? '&' : '?') + params.toString();
        };
      }
    }

    // ---- event delegation for the whole quiz root ----
    root.addEventListener('click', function (e) {
      var optBtn = e.target.closest('.rp-quiz__option');
      if (optBtn) {
        var stepEl = optBtn.closest('.rp-quiz__step');
        var stepId = stepEl.getAttribute('data-step-id');
        var step = cfg.steps.find(function (s) { return s.id === stepId; });
        var group = optBtn.getAttribute('data-group') || step.var;
        var value = optBtn.getAttribute('data-value');
        var multi = optBtn.getAttribute('data-multi') === 'true';
        var exclusive = optBtn.getAttribute('data-exclusive') === 'true';

        if (multi) {
          var arr = (state.answers[group] || []).slice();
          if (exclusive) {
            // Clear everything else; the toggle below then adds or removes this option.
            arr = arr.indexOf(value) > -1 ? [value] : [];
            stepEl.querySelectorAll('[data-group="' + group + '"]').forEach(function (b) {
              if (b !== optBtn) b.classList.remove('is-selected');
            });
          } else {
            stepEl.querySelectorAll('[data-group="' + group + '"][data-exclusive="true"]').forEach(function (b) {
              b.classList.remove('is-selected');
              var exVal = b.getAttribute('data-value');
              var exIdx = arr.indexOf(exVal);
              if (exIdx > -1) arr.splice(exIdx, 1);
            });
          }
          var idx = arr.indexOf(value);
          if (idx > -1) { arr.splice(idx, 1); optBtn.classList.remove('is-selected'); }
          else { arr.push(value); optBtn.classList.add('is-selected'); }
          state.answers[group] = arr;

          var continueBtn = stepEl.querySelector('.rp-quiz__cta[data-role="continue"]');
          if (continueBtn && step.require_selection) {
            continueBtn.disabled = arr.length === 0;
          }
        } else {
          stepEl.querySelectorAll('[data-group="' + group + '"]').forEach(function (b) { b.classList.remove('is-selected'); });
          optBtn.classList.add('is-selected');
          state.answers[group] = value;
          if (step.type === 'compound_age_breed') {
            var otherGroupDone = group === 'dog_age' ? !!state.answers.breed : !!state.answers.dog_age;
            if (otherGroupDone) {
              var cont = stepEl.querySelector('.rp-quiz__cta[data-role="continue"]');
              if (cont) cont.disabled = false;
            }
          } else if (step.auto_advance || step.type === 'landing') {
            setTimeout(function () { nextFrom(stepId); }, 220);
          }
        }
        return;
      }

      var planCard = e.target.closest('[data-role="plan-card-default"], [data-role="plan-card-alt"]');
      if (planCard) {
        state.answers.selected_plan = planCard.getAttribute('data-role') === 'plan-card-alt' ? 'alt' : 'default';
        var offerStepEl = planCard.closest('.rp-quiz__step');
        var offerStep = cfg.steps.find(function (s) { return s.id === offerStepEl.getAttribute('data-step-id'); });
        renderOffer(offerStep, offerStepEl);
        logEvent('plan_selected', { step_id: offerStep.id, selected_plan: state.answers.selected_plan });
        return;
      }

      var continueBtn2 = e.target.closest('.rp-quiz__cta[data-role="continue"]');
      if (continueBtn2) {
        var stepEl2 = continueBtn2.closest('.rp-quiz__step');
        var textInput = stepEl2.querySelector('.rp-quiz__input');
        if (textInput) {
          var step2 = cfg.steps.find(function (s) { return s.id === stepEl2.getAttribute('data-step-id'); });
          state.answers[step2.var] = textInput.value.trim() || (cfg.fallbacks && cfg.fallbacks[step2.var]) || '';
        }
        nextFrom(stepEl2.getAttribute('data-step-id'));
        return;
      }

      var skipBtn = e.target.closest('.rp-quiz__skip');
      if (skipBtn) {
        var stepEl3 = skipBtn.closest('.rp-quiz__step');
        nextFrom(stepEl3.getAttribute('data-step-id'));
        return;
      }

      var backBtn = e.target.closest('.rp-quiz__back');
      if (backBtn) { back(); return; }

      var advanceBtn = e.target.closest('[data-role="advance"]');
      if (advanceBtn) {
        var stepEl4 = advanceBtn.closest('.rp-quiz__step');
        nextFrom(stepEl4.getAttribute('data-step-id'));
        return;
      }
    });

    // loading-step auto-advance
    cfg.steps.forEach(function (s) {
      if (s.type === 'loading' || s.type === 'plan_loading') {
        var el = stepEls[s.id];
        if (!el) return;
        var observer = new MutationObserver(function () {
          if (el.classList.contains('is-active')) {
            setTimeout(function () { nextFrom(s.id); }, s.duration_ms || 2200);
          }
        });
        observer.observe(el, { attributes: true, attributeFilter: ['class'] });
      }
    });

    // kick off
    if (cfg.steps.length) {
      logEvent('quiz_start', {});
      goTo(cfg.steps[0].id);
    }
    initProof(root);
  }

  // Landing review carousel: endless loop via one clone at each end, centred active card.
  function initProof(root) {
    root.querySelectorAll('[data-proof]').forEach(function (wrap) {
      var track = wrap.querySelector('[data-proof-track]');
      var real = Array.prototype.slice.call(track.querySelectorAll('[data-proof-card]'));
      var dots = Array.prototype.slice.call(wrap.querySelectorAll('.rp-quiz__proof-dot'));
      var n = real.length;
      if (!n) return;
      if (n > 1) {
        var headClone = real[n - 1].cloneNode(true), tailClone = real[0].cloneNode(true);
        [headClone, tailClone].forEach(function (c) {
          c.setAttribute('aria-hidden', 'true');
          c.querySelectorAll('button').forEach(function (b) { b.tabIndex = -1; });
        });
        track.insertBefore(headClone, real[0]);
        track.appendChild(tailClone);
      }
      var cards = Array.prototype.slice.call(track.querySelectorAll('[data-proof-card]'));
      var active = n > 1 ? 1 : 0;
      var paused = false;

      function realIndex(i) { return n > 1 ? (i - 1 + n) % n : 0; }
      function centerLeft(c) { return c.offsetLeft - (track.clientWidth - c.offsetWidth) / 2; }
      function setActive(i) {
        active = i;
        cards.forEach(function (c, j) { c.classList.toggle('is-active', j === i); });
        var r = realIndex(i);
        dots.forEach(function (d, j) { d.classList.toggle('is-active', j === r); d.setAttribute('aria-current', j === r ? 'true' : 'false'); });
      }
      function go(i, smooth) {
        if (i < 0 || i >= cards.length) { i = n > 1 ? (i < 0 ? n : 1) : 0; smooth = false; }
        track.scrollTo({ left: centerLeft(cards[i]), behavior: smooth ? 'smooth' : 'auto' });
        setActive(i);
      }
      function checkMore(c) {
        var q = c.querySelector('.rp-quiz__proof-quote'), b = c.querySelector('[data-proof-more]');
        if (!q || !b) return;
        b.hidden = !c.classList.contains('is-open') && q.scrollHeight <= q.clientHeight + 2;
      }
      function refresh() { if (track.clientWidth) { go(active, false); cards.forEach(checkMore); } }

      var raf, settle;
      track.addEventListener('scroll', function () {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          var mid = track.scrollLeft + track.clientWidth / 2, best = active, bd = Infinity;
          cards.forEach(function (c, j) { var d = Math.abs(c.offsetLeft + c.offsetWidth / 2 - mid); if (d < bd) { bd = d; best = j; } });
          if (best !== active) setActive(best);
        });
        clearTimeout(settle);
        settle = setTimeout(function () {
          if (n > 1 && (active === 0 || active === cards.length - 1)) go(active === 0 ? n : 1, false);
        }, 160);
      }, { passive: true });

      dots.forEach(function (d, j) { d.addEventListener('click', function () { paused = true; go(n > 1 ? j + 1 : 0, true); }); });
      wrap.addEventListener('click', function (e) {
        var b = e.target.closest('[data-proof-more]');
        if (!b) return;
        var c = b.closest('[data-proof-card]'), open = c.classList.toggle('is-open');
        b.textContent = open ? 'Show less' : 'Read more';
        paused = true;
      });

      var secs = parseFloat(wrap.getAttribute('data-autoplay')) || 0;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (secs > 0 && n > 1 && !reduce) {
        ['pointerdown', 'touchstart', 'focusin'].forEach(function (ev) { wrap.addEventListener(ev, function () { paused = true; }, { passive: true }); });
        wrap.addEventListener('mouseenter', function () { paused = true; });
        wrap.addEventListener('mouseleave', function () { paused = false; });
        setInterval(function () {
          var step = wrap.closest('.rp-quiz__step');
          if (paused || document.hidden || !track.clientWidth || (step && !step.classList.contains('is-active'))) return;
          if (n > 1 && active >= cards.length - 1) go(1, false);
          go(active + 1, true);
        }, secs * 1000);
      }
      refresh();
      window.addEventListener('load', refresh);
      // Carousels on hidden steps measure 0 wide; re-centre when their step is shown.
      var ownStep = wrap.closest('.rp-quiz__step');
      if (ownStep && window.MutationObserver) {
        new MutationObserver(function () {
          if (ownStep.classList.contains('is-active')) refresh();
        }).observe(ownStep, { attributes: true, attributeFilter: ['class'] });
      }
      var rt;
      window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(refresh, 120); });
    });
  }

  function boot() {
    document.querySelectorAll('.rp-quiz[data-quiz-id]').forEach(initQuiz);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('.rp-quiz[data-quiz-id]') : null;
    if (root) initQuiz(root);
  });
})();

// Video testimonial sections placed in the template after the quiz are moved into the
// [data-mount-key] slots inside their steps. Their carousel measures card widths when it
// starts, so its init is held back until the slot's step is first shown.
(function () {
  document.addEventListener('DOMContentLoaded', function () {
    var held = {};
    Array.prototype.forEach.call(document.querySelectorAll('[data-mount-key]'), function (mount) {
      var sec = document.querySelector('.shopify-section[id$="__' + mount.getAttribute('data-mount-key') + '"]');
      if (!sec) return;
      mount.appendChild(sec);
      var vt = sec.querySelector('[id^="video-testimonials-"]');
      if (vt) held[vt.id.slice('video-testimonials-'.length)] = mount.closest('.rp-quiz__step');
    });
    var orig = window.initVideoTestimonials;
    if (typeof orig !== 'function') return;
    window.initVideoTestimonials = function (id) {
      if (!(id in held)) return orig(id);
      var step = held[id];
      delete held[id];
      if (!step || step.classList.contains('is-active')) return orig(id);
      var mo = new MutationObserver(function () {
        if (!step.classList.contains('is-active')) return;
        mo.disconnect();
        orig(id);
      });
      mo.observe(step, { attributes: true, attributeFilter: ['class'] });
    };
  });
})();
